// Value Chain Calculator — calculation core, moved verbatim from the former
// public/calculators/value-chain.html. Inputs stay strings (like DOM values)
// so parseFloat fallbacks and saved snapshots behave exactly as before.

export const DEFAULT_VALUES = {
  customerName: "", probability: "50", startDate: "", paymentTerms: "30",
  numStores: "500", unitsPerStoreWeek: "1.5", activeWeeks: "52", pipelineFill: "1000",
  distPct: "25", brokerPct: "4", royaltyPct: "0", miscPct: "2", promoPct: "8", termsPct: "2",
  slotting: "0", promoFees: "0", annualFees: "0", tradeshows: "0",
};
export const INPUT_IDS = Object.keys(DEFAULT_VALUES);
export const DEFAULT_PRODUCT = { name: "Product SKU 1", price: "3.99", cogs: "1.20", royalty: "0", units: "25000" };

export const CHANNEL_TIPS = {
  retail: '<div class="warn-box">📋 <strong>Retail tip:</strong> Retailers demand FOB (freight on board) delivery to their DC. Budget 20–30% distribution, 2–3% deductions, and 8–15% trade spend. Gross margin must exceed 35–40% to leave room for a viable net.</div>',
  distributor: '<div class="warn-box">📋 <strong>Distributor tip:</strong> Distributors buy at a discount from your invoice price and resell to retailers. Your "invoice price" here should reflect your sell-in price to the distributor. They typically take 18–30% gross margin. Set distribution % to reflect their markup over your cost.</div>',
  wholesale: '<div class="warn-box">📋 <strong>Wholesale tip:</strong> Wholesale buyers often purchase in large quantities at low margins. Distribution costs are lower (single drop ship), but invoice prices are also lower. Focus on volume efficiency and minimal fixed cost exposure.</div>',
  ecommerce: '<div class="warn-box">📋 <strong>E-Commerce tip:</strong> Include platform fees (15–20% for Amazon/FBA), fulfillment costs, and returns in your distribution %. Digital promotions (coupons, sponsored ads) can run 10–20% of revenue. Payment terms are typically Net 14–30.</div>',
};

const pf = (x) => parseFloat(x) || 0;

export function fmt(n, dec = 0) {
  if (isNaN(n) || n === null) return "—";
  return n.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}
function fmtDollar(n) {
  if (isNaN(n) || n === null) return "—";
  const abs = Math.abs(n);
  if (abs >= 1000000) return "$" + (n / 1000000).toFixed(2) + "M";
  if (abs >= 1000) return "$" + fmt(n, 0);
  return "$" + n.toFixed(2);
}
function fmtDollarFull(n) {
  if (isNaN(n)) return "—";
  return (n < 0 ? "-$" : "$") + fmt(Math.abs(n), 0);
}
function fmtPct(n) {
  if (isNaN(n) || !isFinite(n)) return "—";
  return n.toFixed(1) + "%";
}

const productRows = (products) => products.map((p) => ({
  name: p.name, price: pf(p.price), cogs: pf(p.cogs), royalty: pf(p.royalty), units: pf(p.units),
}));

function volume(v) {
  const stores = pf(v.numStores);
  const uspw = pf(v.unitsPerStoreWeek);
  const weeks = parseFloat(v.activeWeeks) || 52;
  const pipeline = pf(v.pipelineFill);
  const weekly = stores * uspw;
  const recurring = weekly * weeks;
  return { weekly, recurring, pipeline, total: recurring + pipeline };
}

// Returns every output text + the dynamic class names, keyed by element id.
export function compute(v, products, channel) {
  const rows = productRows(products);
  const vol = volume(v);
  const totalVol = vol.total;

  let totalSales = 0, totalCOGS = 0, totalRoyaltyUnits = 0;
  let totalProductUnits = 0;
  rows.forEach(r => {
    const units = r.units > 0 ? r.units : (rows.length === 1 ? totalVol : 0);
    totalSales += r.price * units;
    totalCOGS += r.cogs * units;
    totalRoyaltyUnits += (r.royalty / 100) * r.price * units;
    totalProductUnits += units;
  });
  if (totalProductUnits === 0 && rows.length > 0) {
    const avgPrice = rows.reduce((a, r) => a + r.price, 0) / rows.length;
    const avgCogs = rows.reduce((a, r) => a + r.cogs, 0) / rows.length;
    totalSales = avgPrice * totalVol;
    totalCOGS = avgCogs * totalVol;
  }

  const grossMargin = totalSales - totalCOGS;
  const gmPct = totalSales > 0 ? (grossMargin / totalSales) * 100 : 0;

  const distCost = totalSales * pf(v.distPct) / 100;
  const royaltyCost = totalSales * pf(v.royaltyPct) / 100 + totalRoyaltyUnits;
  const brokerCost = totalSales * pf(v.brokerPct) / 100;
  const miscCost = totalSales * pf(v.miscPct) / 100;
  const promoCost = totalSales * pf(v.promoPct) / 100;
  const termsCost = totalSales * pf(v.termsPct) / 100;
  const totalCoS = distCost + royaltyCost + brokerCost + miscCost + promoCost + termsCost;
  const cosPct = totalSales > 0 ? (totalCoS / totalSales) * 100 : 0;
  const netRevenue = totalSales - totalCOGS - totalCoS;

  const totalFixed = pf(v.slotting) + pf(v.promoFees) + pf(v.annualFees) + pf(v.tradeshows);
  const netProfit = netRevenue - totalFixed;
  const npPct = totalSales > 0 ? (netProfit / totalSales) * 100 : 0;

  const prob = parseFloat(v.probability) || 50;
  const weightedRevenue = totalSales * prob / 100;

  const allUnits = totalProductUnits > 0 ? totalProductUnits : totalVol;
  const realizedPerUnit = allUnits > 0 ? (netRevenue / allUnits) : 0;
  const netProfitPerUnit = allUnits > 0 ? (netProfit / allUnits) : 0;
  const marginPerUnit = allUnits > 0 ? ((grossMargin - totalCoS) / allUnits) : 0;
  const breakEven = marginPerUnit > 0 ? Math.ceil(totalFixed / marginPerUnit) : 0;

  const neg = (n) => (n > 0 ? "(" + fmtDollarFull(n) + ")" : "—");
  const customer = v.customerName || "Opportunity";
  return {
    "calc-weekly": fmt(vol.weekly, 0), "calc-recurring": fmt(vol.recurring, 0),
    "calc-pipeline": fmt(vol.pipeline, 0), "calc-total": fmt(vol.total, 0),
    "opp-name-display": customer + " · " + channel.charAt(0).toUpperCase() + channel.slice(1),
    "wf-sales": fmtDollarFull(totalSales), "wf-cogs": neg(totalCOGS),
    "wf-gm": fmtDollarFull(grossMargin), "wf-gm-pct": fmtPct(gmPct),
    "wf-dist": neg(distCost), "wf-royalty": neg(royaltyCost), "wf-broker": neg(brokerCost),
    "wf-misc": neg(miscCost), "wf-promo": neg(promoCost), "wf-terms": neg(termsCost),
    "wf-netr": fmtDollarFull(netRevenue), "wf-fixed": neg(totalFixed),
    netProfitDisplay: fmtDollarFull(netProfit),
    netPctDisplay: fmtPct(npPct) + " net margin",
    "stat-gm": fmtPct(gmPct), "stat-np": fmtPct(npPct), "stat-units": fmt(allUnits, 0),
    "m-gm": fmtPct(gmPct), "m-cos": fmtPct(cosPct), "m-weighted": fmtDollar(weightedRevenue),
    "m-breakeven": breakEven > 0 ? fmt(breakEven, 0) : "—",
    "m-realized": realizedPerUnit !== 0 ? "$" + realizedPerUnit.toFixed(2) : "—",
    "m-npunit": netProfitPerUnit !== 0 ? "$" + netProfitPerUnit.toFixed(2) : "—",
    cls: {
      netProfitDisplay: "net-amount" + (netProfit < 0 ? " negative" : ""),
      netPctDisplay: "net-pct " + (npPct > 10 ? "positive" : npPct < 0 ? "negative" : "zero"),
      "m-gm": "m-val " + (gmPct >= 35 ? "amber" : gmPct < 20 ? "red" : ""),
      "m-npunit": "m-val " + (netProfitPerUnit > 0 ? "teal" : netProfitPerUnit < 0 ? "red" : ""),
    },
  };
}

export function buildCsv(v, products, channel, o) {
  const customer = v.customerName || "Opportunity";
  const rows = productRows(products);
  let csv = "CPG Value Chain Calculator — Export\n\n";
  csv += "OPPORTUNITY DETAILS\n";
  csv += `Customer,${customer}\nChannel,${channel}\nProbability,${v.probability}%\nStart Date,${v.startDate}\n\n`;
  csv += "PRODUCTS\nSKU,Invoice Price,COGS,Royalty %,Units/Year\n";
  rows.forEach(r => { csv += `${r.name},${r.price},${r.cogs},${r.royalty},${r.units}\n`; });
  csv += "\nVOLUME\n";
  csv += `Stores,${v.numStores}\nUnits/Store/Week,${v.unitsPerStoreWeek}\nActive Weeks,${v.activeWeeks}\nPipeline Fill,${v.pipelineFill}\n`;
  csv += "\nVALUE CHAIN DEDUCTIONS\n";
  csv += `Distribution %,${v.distPct}%\nBroker %,${v.brokerPct}%\nRoyalty % of Sales,${v.royaltyPct}%\nMisc Deductions %,${v.miscPct}%\nPromotions %,${v.promoPct}%\nTerms Discount %,${v.termsPct}%\n`;
  csv += "\nFIXED COSTS\n";
  csv += `Slotting,$${v.slotting}\nPromo Fees,$${v.promoFees}\nAnnual Fees,$${v.annualFees}\nTrade Shows,$${v.tradeshows}\n`;
  csv += "\nRESULTS\n";
  csv += `Gross Margin %,${o["m-gm"]}\n`;
  csv += `Net Revenue,${o["wf-netr"]}\n`;
  csv += `Net Profit,${o.netProfitDisplay}\n`;
  csv += `Net Margin %,${o.netPctDisplay}\n`;
  csv += `Weighted Revenue,${o["m-weighted"]}\n`;
  return { csv, filename: `value-chain-${customer.replace(/\s+/g, "-").toLowerCase()}.csv` };
}
